import React, { useCallback, useMemo, useState } from "react";

import { faPencil, faPlus } from "@fortawesome/free-solid-svg-icons";
import { faBook } from "@fortawesome/pro-light-svg-icons";
import { faFiles } from "@fortawesome/pro-regular-svg-icons";
import { useDispatch, useSelector } from "react-redux";

import { openDispatchingModal } from "actions";

import AgGridTable from "components/AgGrid/AgGridContainer";
import { Column } from "components/Layout";

import { AgGridTables } from "constants/aggrid";
import { ModalTypes } from "constants/navigation";
import {
  DeploymentPermissions,
  SaleyardPermissions,
} from "constants/permissions";

import {
  openCreateGlobalBusinessModal,
  openCreateSideLoadedBusinessModal,
  openEditBusinessModal,
  openModalLink,
} from "lib/navigation";
import { pluralize } from "lib/pluralize";

import {
  getDefaultBillingSettings,
  getIsLivestockAgent,
  getProperties,
  selectDefaultBuyerSplitByBusinessIdLookup,
  selectDefaultVendorSplitByBusinessIdLookup,
} from "selectors";

import { selectBusinessesAggridData } from "selectors/aggrid/businesses";

import {
  useDebounceSelector,
  useHasBillingPermission,
  useHasDeploymentPermission,
  useHasExportBusinessesPermission,
  useHasSaleyardOrDeploymentPermission,
  useHasSideLoadedBusinessPermission,
  useIsMobile,
} from "hooks";

import { columnsDefs } from "./BusinessListColumns";
import { faDownload } from "@fortawesome/pro-solid-svg-icons";

const getRowId = params => params.data.business.id;

const additionalActions = [
  {
    default: true,
    title: "Add Business",
    isDisabled: false,
    onClick: () => openEditBusinessModal(),
    icon: faPlus,
    dataTour: "addBusiness",
  },
];

export function BusinessTable() {
  const dispatch = useDispatch();
  const rowData = useDebounceSelector(selectBusinessesAggridData);
  const propertyByIdLookup = useSelector(getProperties);
  const defaultVendorSplitByBusinessIdLookup = useSelector(
    selectDefaultVendorSplitByBusinessIdLookup,
  );
  const defaultBuyerSplitByBusinessIdLookup = useSelector(
    selectDefaultBuyerSplitByBusinessIdLookup,
  );
  const hasExportCsvPermissions = useHasExportBusinessesPermission();
  const context = useMemo(
    () => ({
      defaultVendorSplitByBusinessIdLookup,
      defaultBuyerSplitByBusinessIdLookup,
      propertyByIdLookup,
    }),
    [
      defaultVendorSplitByBusinessIdLookup,
      defaultBuyerSplitByBusinessIdLookup,
      propertyByIdLookup,
    ],
  );

  const isMobile = useIsMobile();
  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const hasInterestFeature = useHasDeploymentPermission(
    DeploymentPermissions.featureInterest,
  );
  const hasBillingPermission = useHasBillingPermission();
  const enableInterest = useSelector(getDefaultBillingSettings)?.enableInterest;
  const isInterestEnabled = useMemo(
    () => hasInterestFeature && enableInterest,
    [hasInterestFeature, enableInterest],
  );

  const hasMyobExportsFeature = useHasDeploymentPermission(
    DeploymentPermissions.featureMyobIntegration,
  );
  const [selectedBusinessIds, setSelectedBusinessIds] = useState([]);

  const selectedBusinessIdsCount = selectedBusinessIds.length;
  const onRowSelectionChange = useCallback(
    rows => {
      const newSelectedBusinessIds = rows.map(r => r.business.id);
      if (
        newSelectedBusinessIds.length !== selectedBusinessIdsCount ||
        !newSelectedBusinessIds.every(id => selectedBusinessIds.includes(id))
      ) {
        setSelectedBusinessIds(newSelectedBusinessIds);
      }
    },
    [selectedBusinessIdsCount, selectedBusinessIds, setSelectedBusinessIds],
  );

  const defaultCsvExportParams = useMemo(
    () => ({
      // only export selected rows to the export csv - if no rows are selected export all
      onlySelected: !!selectedBusinessIdsCount,
    }),
    [selectedBusinessIdsCount],
  );

  const openEditBusinessesModal = useCallback(() => {
    dispatch(
      openDispatchingModal(
        ModalTypes.BulkUpdateBusiness,
        window.location.hash,
        {
          businessIds: selectedBusinessIds,
        },
      ),
    );
  }, [dispatch, selectedBusinessIds]);

  const onClickGenerateReports = useCallback(() => {
    openModalLink(ModalTypes.BusinessReports, {
      businessIds: selectedBusinessIds,
    });
  }, [selectedBusinessIds]);

  const hasBulkUpdateBusinessPermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureBulkUpdateBusinesses,
    SaleyardPermissions.featureBulkUpdateBusinesses,
  );
  const hasCreateGlobalBusinessFeature = useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureGlobalBusinessSearch,
    SaleyardPermissions.featureGlobalBusinessSearch,
  );

  const hasSideLoadBusinessFeature = useHasSideLoadedBusinessPermission();

  const trimmedActions = useMemo(() => {
    const actions = [...additionalActions];

    if (isLivestockAgent && hasBillingPermission) {
      actions.push({
        default: true,
        title: `Generate ${pluralize("Report", selectedBusinessIdsCount)} for ${selectedBusinessIdsCount} ${pluralize("Business", selectedBusinessIdsCount)}`,
        isDisabled: selectedBusinessIdsCount === 0,
        onClick: onClickGenerateReports,
        icon: faFiles,
        dataTour: "generateReports",
      });
    }
    if (hasCreateGlobalBusinessFeature) {
      actions.push({
        title: "Add Connected Business",
        isDisabled: false,
        onClick: () => openCreateGlobalBusinessModal(),
        icon: faBook,
        dataTour: "addGlobalBusiness",
      });
    }
    if (hasSideLoadBusinessFeature) {
      actions.push({
        title: "Add Business from Legacy Data",
        isDisabled: false,
        onClick: () => openCreateSideLoadedBusinessModal(),
        icon: faDownload,
        dataTour: "addSideLoadedBusiness",
      });
    }
    if (hasBulkUpdateBusinessPermission) {
      actions.push({
        dataTour: "bulkUpdate",
        title: `Bulk Update ${selectedBusinessIdsCount} Businesses`,
        onClick: () => openEditBusinessesModal(),
        icon: faPencil,
        isDisabled: selectedBusinessIdsCount === 0,
      });
    }
    return actions;
  }, [
    hasCreateGlobalBusinessFeature,
    hasBillingPermission,
    hasBulkUpdateBusinessPermission,
    isLivestockAgent,
    openEditBusinessesModal,
    onClickGenerateReports,
    selectedBusinessIdsCount,
    hasSideLoadBusinessFeature,
  ]);

  const memoizedColumnDefs = useMemo(
    () =>
      columnsDefs(isLivestockAgent, isInterestEnabled, hasMyobExportsFeature),
    [isLivestockAgent, isInterestEnabled, hasMyobExportsFeature],
  );

  return (
    <Column fullHeight>
      <AgGridTable
        additionalActions={trimmedActions}
        columnDefs={memoizedColumnDefs}
        context={context}
        getRowId={getRowId}
        rowData={rowData}
        tableName={AgGridTables.BUSINESSES}
        showTextFilter
        cacheQuickFilter
        hasExportCsvPermissions={hasExportCsvPermissions}
        suppressCellSelection={!!isMobile}
        hideSideBar={!!isMobile}
        hideSavedViews={!!isMobile}
        rowsSelectable
        onRowSelectionChanged={onRowSelectionChange}
        rowSelection="multiple"
        defaultCsvExportParams={defaultCsvExportParams}
      />
    </Column>
  );
}
